import React, { useCallback, useRef } from 'react';

import { toTitleCase } from '../../../utils/stringUtils';

interface TextFieldProps {
  id: string;
  type: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  onClick?: () => void;
  textCase?:
    | 'sentencecase'
    | 'titlecase'
    | 'uppercase'
    | 'lowercase'
    | 'standard';
}

const toSentenceCase = (value: string): string => {
  let shouldCapitalize = true;
  let result = '';

  for (let i = 0; i < value.length; i++) {
    const char = value[i];
    const isLetter = /[a-zA-Z]/.test(char);

    if (isLetter) {
      result += shouldCapitalize ? char.toUpperCase() : char.toLowerCase();
      shouldCapitalize = false;
      continue;
    }

    result += char;
    if (
      char === '.' ||
      char === '!' ||
      char === '?' ||
      char === '\n' ||
      char === '\r'
    ) {
      shouldCapitalize = true;
    }
  }

  return result;
};

const TextField: React.FC<TextFieldProps> = ({
  id,
  type,
  value,
  onChange,
  placeholder,
  disabled,
  maxLength,
  onClick,
  textCase = 'standard',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Apply lowercase to text and email inputs; other transformations only to text inputs.
      const canTransform =
        textCase === 'lowercase'
          ? type === 'text' || type === 'email'
          : type === 'text';

      if (!canTransform || textCase === 'standard') {
        onChange(e);
        return;
      }

      const rawValue = e.target.value ?? '';
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;

      const transformedValue =
        textCase === 'uppercase'
          ? rawValue.toUpperCase()
          : textCase === 'lowercase'
            ? rawValue.toLowerCase()
            : textCase === 'titlecase'
              ? toTitleCase(rawValue)
              : toSentenceCase(rawValue);

      if (transformedValue !== rawValue) {
        // Mutate the input's value so downstream handlers see the transformed value.
        e.target.value = transformedValue;
      }

      onChange(e);

      // Preserve cursor position for controlled inputs.
      if (
        transformedValue !== rawValue &&
        selectionStart !== null &&
        selectionEnd !== null
      ) {
        requestAnimationFrame(() => {
          const el = inputRef.current;
          if (!el) {
            return;
          }
          try {
            el.setSelectionRange(selectionStart, selectionEnd);
          } catch {
            // Some input types may not support selection; safe to ignore.
          }
        });
      }
    },
    [textCase, onChange, type],
  );

  return (
    <input
      ref={inputRef}
      type={type}
      id={id}
      value={value}
      onChange={handleChange}
      onClick={onClick}
      placeholder={placeholder}
      maxLength={maxLength}
      className="h-10 w-full rounded-md border border-gray-300 px-4 text-sm text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-primary focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-ink-muted dark:border-white/20 dark:bg-white/5 dark:text-white dark:placeholder:text-white/40 dark:focus:border-primary"
      disabled={disabled}
    />
  );
};

export default TextField;
